import { RefObject } from 'react';

import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

import { IBundle } from '~/src/entities/tariff';
import { ICardBenefits } from '~/src/features/movix-new/BenefitsCard';
import { BAR_WIDTH, BOUNDING_RECT_FIXED, ELABELS, ZERO } from '~/src/features/movix-new/constants';
import { dataBenefitsBlockOne, dataBenefitsBlockThree } from '~/src/features/movix-new/data';

interface ISeoPageData {
    title: string,
    description: string,
}

export interface IZasmotrisData {
    price: number,
    afterPrice: number,
    salePackageId: number,
}

/**
 * Возвращает SEO-данные
 *
 * @function
 * @name utils.getPageDataSeo
 * @param {ICurrentCity} city - данные о городе и тп.
 * @returns {ISeoPageData}
 */
export const getPageDataSeo = (city: ICurrentCity): ISeoPageData => {
    return {
        title: `Купить ТВ-приставку Movix от Дом.ру в ${city.cityIn} | Андроид приставка для телевизора`,
        description:
            'Провайдер Дом.ру предлагает получить ТВ-приставку Movix, подключив тариф «Засмотрись», ' +
            'взять бесплатно на 2-х недельный тест-драйв или купить. ' +
            `Подробнее на сайте или по ☎ ${city.phone}`,
    };
};

/**
 * Проверка нахождения элемента плашки хедера в зоне контента страницы
 *
 * @function
 * @name utils.checkCollision
 * @param {RefObject<HTMLHeadElement>} containerContentRef - реф контейнера контента страницы
 * @returns {boolean}
 */
export const checkCollision = (containerContentRef: RefObject<HTMLHeadElement>): boolean => {
    const barPosition = BOUNDING_RECT_FIXED + window.scrollY + BAR_WIDTH;
    const headerOffsetHeight = containerContentRef?.current?.offsetHeight || ZERO;
    const headerBoundingClientRect = containerContentRef?.current?.getBoundingClientRect().top || ZERO;
    const containerContext = headerOffsetHeight + headerBoundingClientRect + window.scrollY;
    return barPosition < containerContext;
};

/**
 * Проверка на браузер Safari
 *
 * @function
 * @name utils.isSafari
 * @returns {boolean}
 */
export const isSafari = (): boolean => {
    enum agent {
        safari = 'safari',
        chrome = 'chrome',
    }
    const INDEX_FAIL_CODE = -1;
    const ua = navigator.userAgent.toLowerCase();
    return ua.indexOf(agent.safari) > INDEX_FAIL_CODE && ua.indexOf(agent.chrome) < ZERO;
};

/**
 * Проверка на браузер типа Chromium
 *
 * @function
 * @name utils.checkChrome
 * @returns {boolean}
 */
export const isChromium = (): boolean => {
    let browserName = '';
    const userAgent = navigator.userAgent;
    // @ts-ignore
    (!!window.chrome && !userAgent.match(/(OPR)/)) && (browserName = 'Chrome');
    // @ts-ignore
    (!!window.chrome && userAgent.match(/Edge|Edg/)) && (browserName = 'Edge');
    // @ts-ignore
    (!!window.chrome && userAgent.search(/YaBrowser/) > ZERO) && (browserName = 'Yandex');
    return browserName === 'Chrome';
};

/**
 * Возвращает данные для третьего блока BenefitsMovix
 *
 * @function
 * @name utils.getBenefitsCards
 * @param {boolean} withCardEconomy - необходимо ли включить в дату блок "Для Экономных"
 * @returns {ICardBenefits[]}
 */
export const getBenefitsCards = (withCardEconomy: boolean): ICardBenefits[] => {
    const cardEconomy = <ICardBenefits>[...dataBenefitsBlockOne].pop();
    if (withCardEconomy) {
        return [...dataBenefitsBlockThree, cardEconomy];
    }
    return dataBenefitsBlockThree;
};

/**
 * Отправляет события аналитики - обертка
 *
 * @function
 * @name utils.dataLayerPushButton
 * @param {ELABELS} label - описание места отправки события
 * @returns {void}
 */
export const dataLayerPushButton = (label: ELABELS): void => {
    dataLayerPush({
        event: EVENTS.UAevent,
        category: CATEGORIES.landing,
        action: ACTIONS.clickOnBtn,
        label,
    });
};

export const getZasmotrisData = (bundle?: IBundle): IZasmotrisData | null => {
    if (bundle) {
        return {
            price: calculatePrice(bundle),
            afterPrice: calculatePrice(bundle, true),
            salePackageId: bundle.sale_package_id,
        };
    }

    return null;
};

const calculatePrice = ({ salePackage2in1 }: IBundle, isAfterPrice?: boolean) => {
    const { price_final, promo_price_final } = salePackage2in1;
    return isAfterPrice ? price_final : promo_price_final;
};
